<template>
    <ConfirmDialog :group="tableDialogGroup">
        <template #container="{ message, acceptCallback, rejectCallback }">
            <div class="rounded rounded-10">
                <div class="modal-content">
                    <div :class="'modal-header modal-colored-header bg-' + message.color + ' text-white'">
                        <h4 class="mt-2 text-white">{{ message.header }}</h4>
                        <button type="button" class="close" aria-hidden="true" @click="rejectCallback">×</button>
                    </div>
                    <div class="modal-body">
                        <p class="mt-3">{{ message.message }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button"
                                @click="acceptCallback"
                                :class="'btn waves-effect btn-' + message.color + ' waves-light text-white my-2'">
                            <i :class="message.icon" aria-hidden="true"></i>
                            {{ message.header }}
                        </button>
                        <button type="button"
                                @click="rejectCallback"
                                class="btn waves-effect waves-light btn-light my-2">
                            <i class="fas fa-ban"></i> Cancel
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </ConfirmDialog>
    <Panel
        :class="'card rounded rounded-10'"
        :toggleable="toggleable"
    >
        <template #header class="card-header">
            <h5>
                <i class="fas fa-th-list mr-2"></i> {{ title }}  <a v-if="help" data-toggle="tooltip" data-position="top" :title="help"><i class="fas fa-question-circle"></i></a>
                <span v-if="filterPreset.value">
                        {{filterPreset.information}}
                        <button v-if="filterPreset.value" :class="'btn btn-sm ' + this.pageButton + ' text-white'"
                                @click="removePresetFilters()">
                                    <i class="far fa-filter-slash" title="Clear Filter"></i>
                        </button>
                </span>
            </h5>
        </template>
        <template #toggleicon="toggleButtonProps">
            <button
                :class="'btn btn-sm ' + this.pageButton + ' text-white'"
                @click="toggleButtonProps.onClick"
                type="button"
            >
                <i :class="toggleButtonProps.collapsed ? 'fa-solid fa-plus' : 'fa-solid fa-minus'"></i>
            </button>
        </template>

        <slot name="summary">
            <div v-if="showSummary && summary" class="row justify-content-between">
                <div v-for="(item, index) in summary" :key="index" class="col-md-3 col-sm-6">
                    <h6 v-if="item.value"><i :class="item.icon"/> <b>{{ item.label }}:</b> {{ item.value }}</h6>
                </div>
            </div>
        </slot>

        <div @contextmenu="actionMenu ? onTableContextMenu : () => {}">
            <ContextMenu v-if="actionMenu" ref="tableContextMenuRef" :model="tableMenuModel"/>
            <ContextMenu v-if="actionMenu" ref="rowContextMenuRef" :model="rowMenuModel"/>
            <div class="rounded rounded-10">
                <DataTable
                    v-model:filters="filters"
                    v-model:selection="selectedData"
                    v-model:contextMenuSelection="selectedData"
                    :contextMenu="selectable && selectionMode === 'single'"
                    :value="data"
                    ref="dt"
                    striped-rows
                    show-gridlines
                    scrollable
                    lazy
                    paginator
                    :rows="rows"
                    :rowsPerPageOptions="[5, 10, 25, 50]"
                    :totalRecords="filteredRecords"
                    :loading="loading"
                    @page="onPageChange"
                    @filter="onFilter"
                    @sort="onSortChange"
                    @rowContextmenu="onRowContextMenu"
                    :globalFilterFields="globalFilterFields"
                    :filterDisplay="filterDisplayMode"
                    :dataKey="dataKey"
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                >
                    <template #header>
                        <div class="row justify-content-end">
                            <div>
                                <Select
                                    v-if="monthSelect"
                                    v-model="selectedMonth"
                                    :options="availableMonths"
                                    optionLabel="label"
                                    optionValue="value"
                                    aria-labelledby="basic"
                                />
                            </div>
                        </div>
                        <div class="row justify-content-between pt-2">
                            <div>
                                <MultiSelect
                                    v-model="visibleColumns"
                                    :options="toggleableColumns"
                                    optionLabel="header"
                                    @update:modelValue="onToggleColumns"
                                    :maxSelectedLabels="0"
                                    :showToggleAll="false"
                                >
                                    <template #value="slotProps">
                                        <i class="fas fa-columns"></i>
                                    </template>
                                </MultiSelect>
                            </div>
                            <div>
                                <IconField>
                                    <InputIcon>
                                        <i class="fa-solid fa-magnifying-glass"></i>
                                    </InputIcon>
                                    <InputText v-model="filters.global.value" placeholder="Search" />
                                </IconField>
                            </div>
                        </div>
                        <div v-if="advanced" class="row justify-content-between pt-2">
                            <div>
                                <SelectButton
                                    v-model="advancedFilter"
                                    :options="filterModes"
                                    optionLabel="label"
                                    optionValue="value"
                                    aria-labelledby="basic"
                                />
                            </div>
                            <div>
                                <button v-if="filtersActive"
                                        :class="'btn ' + this.pageButton + ' text-white'"
                                        @click="clearFilter()"
                                >
                                    <i class="fa-regular fa-filter-slash"></i> Clear Filters
                                </button>
                            </div>
                        </div>
                    </template>

                    <template #empty>
                        <div class="d-flex justify-content-center align-items-center py-4">
                            <div class="text-center">
                                <h4 v-if="!loading">No data found.</h4>
                            </div>
                        </div>
                    </template>

                    <template #loading>
                        <div class="d-flex flex-column align-items-center justify-content-center py-4 text-dark">
                            <i class="fa-solid fa-spinner fa-spin fa-4x"></i>
                        </div>
                    </template>
                    <!-- Selectable Column -->
                    <Column v-if="selectable && (selectionMode === 'multiple')"
                            :selectionMode="selectionMode"
                            headerStyle="border-width: 1px 1px 2px 1px; width: 3rem;"
                            frozen />
                    <!-- Data Column -->
                    <Column
                        v-for="(col, index) in visibleColumns"
                        :key="col.field"
                        :header="col.header"
                        :field="col.field"
                        :sortable="col.sortable"
                        :sortField="col.field"
                        :showFilterMenu="col.filter && advancedFilter"
                        :showFilterOperator="advancedFilter"
                        :showFilterMatchModes="advancedFilter"
                        :filter="col.filter"
                        :filterMatchMode="advancedFilter ? null : FilterMatchMode.CONTAINS"
                        :filterMatchModeOptions="getFilterMatchModeOptions(col)"
                        :frozen="col.field === 'actions'"
                        :headerStyle="col.field === 'actions' ? 'border-width: 1px 1px 2px 1px; max-width: 6rem; height: 4.5rem;' : 'border-width: 1px 1px 2px 1px; height: 4.5rem;'"
                        :pt="{
                            filterButtonbar: {
                                style: 'order:-1'
                            }
                        }"
                    >
                        <!-- Advanced Filters Menu -->
                        <template v-if="advancedFilter && col.filter" #filter="{ filterModel }">
                            <InputNumber
                                v-if="col.filters.includes('number')"
                                v-model="filterModel.value"
                            />
                            <DatePicker
                                v-else-if="col.filters.includes('date')"
                                v-model="filterModel.value"
                                dateFormat="yy-mm-dd"
                            />
                            <MultiSelect
                                v-else-if="col.filters.includes('select')"
                                v-model="filterModel.value"
                                :options="col.options"
                                optionLabel="name"
                                :maxSelectedLabels="2"
                                :show-toggle-all="false"
                            >
                                <template #option="slotProps">
                                    <span v-if="slotProps.option.badge" :class="'badge badge-' + col.options.find(option => option.value === slotProps.option.name).badge + ' text-white'">
                                        {{ slotProps.option.name }}
                                    </span>
                                    <span v-else>{{ slotProps.option.value }}</span>
                                </template>
                            </MultiSelect>
                            <InputText v-else v-model="filterModel.value" />
                        </template>
                        <!-- Basic Filters -->
                        <template v-else-if="col.filter" #filter="{ filterModel, filterCallback }">
                            <InputText
                                v-model="filterModel.value"
                                placeholder="Search"
                                style="min-width: 14rem"
                                @input="() => { filterModel.matchMode = FilterMatchMode.CONTAINS; filterCallback(); }"
                            />
                        </template>

                        <template #sorticon="{ sortOrder }">
                            <i v-if="sortOrder === 1" class="fa-solid fa-sort-up"></i>
                            <i v-else-if="sortOrder === -1" class="fa-solid fa-sort-down"></i>
                            <i v-else class="fa-solid fa-sort"></i>
                        </template>

                        <template #filtericon>
                            <i v-if="isFilterActive(col.field)" class="fa-solid fa-filter"></i>
                            <i v-else class="fa-regular fa-filter"></i>
                        </template>

                        <!-- Data Rendering -->
                        <template #body="{ data: rowData }">
                            <span v-if="col.field === 'actions'">
                                <Select
                                    :options="formatActions(rowData[col.field])"
                                    @change="handleAction($event.value, rowData)"
                                    :pt="{
                                        dropdown: {
                                            'class' : 'd-none'
                                        }
                                    }"
                                    class="rounded rounded-10 d-flex justify-content-center align-items-center"
                                >
                                    <template #option="{ option }">
                                        <i :class="option.icon" class="mr-2" />
                                        {{ option.label }}
                                    </template>
                                    <template #value>
                                        <i class="fas fa-ellipsis d-flex justify-content-center align-items-center" />
                                    </template>
                                </Select>
                            </span>
                            <span v-else v-html="renderCell(rowData, col)"></span>
                        </template>
                    </Column>

                    <template #paginatorstart>
                        <span>{{ filteredRecords }} Total Records (filtered from {{ totalRecords }})</span>
                    </template>

                    <template #paginatorend>
                        <button v-if="exportable" :class="'btn ' + this.pageButton + ' text-white'" @click="exportCSV($event)">
                            <i class="fa-regular fa-file-export" /> Export
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </Panel>
</template>

<script>
import {useDatatables} from "../../composables/useDatatable";
// Import necessary components
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import MultiSelect from 'primevue/multiselect';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import Button from 'primevue/button';
import Select from 'primevue/select';
import ContextMenu from 'primevue/contextmenu';
import SelectButton from 'primevue/selectbutton';
import Panel from 'primevue/panel';
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import {FilterMatchMode} from "@primevue/core/api";
import ConfirmDialog from "primevue/confirmdialog";

// Custom components

export default {
    components: {
        DataTable,
        Column,
        InputText,
        MultiSelect,
        InputNumber,
        DatePicker,
        Button,
        Select,
        ContextMenu,
        SelectButton,
        Panel,
        IconField,
        InputIcon,
        ConfirmDialog,
    },
    props: {
        resource: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            required: true,
            default: 'Table',
        },
        rows: {
            type: [Number, String],
            default: 10,
        },
        page: {
            type: [Number, String],
            default: 0,
        },
        dataKey: {
            type: String,
            default: 'id',
        },
        userId: {
            type: [Number, String],
            default: null,
        },
        groupId: {
            type: [Number, String],
            default: null,
        },
        eId: {
            type: String,
            default: null,
        },
        batchId: {
            type: String,
            default: null,
        },
        whitelistId: {
            type: String,
            default: null,
        },
        // Logging
        objectType: {
            type: String,
            default: null,
        },
        objectId: {
            type: String,
            default: null,
        },
        monthSelect: {
            type: Boolean,
            default: false,
        },
        advanced: {
            type: Boolean,
            default: false,
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        actionMenu: {
            type: Boolean,
            default: false,
        },
        exportable: {
            type: Boolean,
            default: false,
        },
        help: {
            String,
            default: null
        },
        showSummary: {
            type: Boolean,
            default: false,
        },
        toggleable: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        tableDialogGroup() {
            return `${this.resource}_${this.type}`;
        },
        filtersActive() {
            return Object.values(this.filters).some(filter => filter.value !== null);
        },
        pageButton() {
            return this.$themeSettings.pageButton;
        },
        pageInfo() {
            return this.$themeSettings.pageInfo;
        },
        pageBox() {
            return this.$themeSettings.pageBox;
        }
    },
    setup(props, context) {

        const {
            // Data & Summary
            data,
            selectedData,
            selectionMode,
            summary,

            // Column Configuration
            columns,
            sortableColumns,
            toggleableColumns,
            visibleColumns,
            onToggleColumns,

            // Loading & Counts
            loading,
            totalRecords,
            filteredRecords,

            // Pagination & Sorting
            rows,
            page,
            onPageChange,
            onSortChange,

            // Preset Filters & Filtering
            filterPreset,
            filters,
            globalFilterFields,
            filtersInitialized,
            filterModes,
            advancedFilter,
            initFilters,
            onFilter,
            clearFilter,
            removePresetFilters,
            isFilterActive,
            getFilterMatchModeOptions,
            filterDisplayMode,

            // Month Selector
            selectedMonth,
            availableMonths,

            // Menus & Actions
            rowMenuModel,
            tableMenuModel,
            onRowContextMenu,
            onTableContextMenu,

            // Cell Rendering & Actions
            renderCell,
            formatActions,
            handleAction,
            performAction,
            view,
            edit,

            // Export
            exportCSV,

            // Utility
            FilterMatchMode,
            rowContextMenuRef,
            tableContextMenuRef,
        } = useDatatables({ ...props }, context);

        return {
            // Data & Summary
            data,
            selectedData,
            selectionMode,
            summary,

            // Column Configuration
            columns,
            sortableColumns,
            toggleableColumns,
            visibleColumns,
            onToggleColumns,

            // Loading & Counts
            loading,
            totalRecords,
            filteredRecords,

            // Pagination & Sorting
            rows,
            page,
            onPageChange,
            onSortChange,

            // Preset Filters & Filtering
            filterPreset,
            filters,
            globalFilterFields,
            filtersInitialized,
            filterModes,
            advancedFilter,
            initFilters,
            onFilter,
            clearFilter,
            removePresetFilters,
            isFilterActive,
            getFilterMatchModeOptions,
            filterDisplayMode,

            // Month Selector
            selectedMonth,
            availableMonths,

            // Menus & Actions
            rowMenuModel,
            tableMenuModel,
            onRowContextMenu,
            onTableContextMenu,

            // Cell Rendering & Actions
            renderCell,
            formatActions,
            handleAction,
            performAction,
            view,
            edit,

            // Export
            exportCSV,

            // Utility
            FilterMatchMode,
            rowContextMenuRef,
            tableContextMenuRef,
        };
    },
};
</script>

<style scoped>
.p-datatable-thead > tr > th {
    border-width: 1px 1px 2px 1px;
}

.p-datatable-header-cell {
    border-width: 1px 1px 2px 1px !important;
}

.p-datatable-mask {
    background-color: rgba(0, 0, 0, 0.4) !important;
}

.p-inputtext {
    font-size: 0.8rem;
}
</style>
